import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Loader2, Plus, RefreshCw, X, ShieldAlert, ClipboardList,
  TrendingUp, Building2, Activity, FileText, CheckCircle2, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCIDENT_TYPES = [
  { value: 'fall', label: 'Fall' },
  { value: 'medication_error', label: 'Medication error' },
  { value: 'near_miss', label: 'Near-miss' },
  { value: 'abuse_suspicion', label: 'Abuse / neglect suspicion' },
  { value: 'infection', label: 'Infection' },
  { value: 'pressure_injury', label: 'Pressure injury' },
  { value: 'equipment_failure', label: 'Equipment failure' },
  { value: 'awol', label: 'Patient AWOL' },
  { value: 'elopement', label: 'Elopement' },
  { value: 'self_harm', label: 'Self-harm' },
  { value: 'vehicle_event', label: 'Vehicle event' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_LABELS = {
  1: '1 · Near-miss',
  2: '2 · No harm',
  3: '3 · Temporary harm',
  4: '4 · Serious harm',
  5: '5 · Permanent / death',
};

const STATUS_LABEL = {
  reported: 'Reported',
  under_review: 'Under review',
  mitigated: 'Mitigated',
  closed: 'Closed',
};

const STATUS_PILL = {
  reported: 'bg-amber-50 text-amber-700 border-amber-200',
  under_review: 'bg-cn-primary/10 text-cn-primary border-cn-primary/20',
  mitigated: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const SEVERITY_PILL = {
  1: 'bg-cn-bone-100 text-cn-text border-cn-border',
  2: 'bg-cn-bone-100 text-cn-text border-cn-border',
  3: 'bg-amber-50 text-amber-700 border-amber-200',
  4: 'bg-orange-50 text-orange-700 border-orange-200',
  5: 'bg-red-50 text-red-700 border-red-200',
};

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Building2, label: 'Referral Sources', href: '/operations/referral-sources' },
  { icon: ShieldAlert, label: 'Incident Reporting', active: true },
];

export default function AdminIncidents() {
  const { getAuthHeader } = useAuth();
  const [days, setDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [focus, setFocus] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadPdf = useCallback(async () => {
    setDownloadingPdf(true);
    try {
      const params = { days };
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = Number(severityFilter);
      const headers = getAuthHeader().headers || {};
      const resp = await axios.get(`${API}/admin/incidents/export.pdf`, {
        headers, params, responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caraloom_incidents_${days}d_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Incident export downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build the PDF');
    } finally {
      setDownloadingPdf(false);
    }
  }, [days, statusFilter, severityFilter, getAuthHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, limit: 200 };
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = Number(severityFilter);
      const [d, l] = await Promise.all([
        axios.get(`${API}/admin/incidents/dashboard`, { ...getAuthHeader(), params: { days } }),
        axios.get(`${API}/incidents`, { ...getAuthHeader(), params }),
      ]);
      setDashboard(d.data);
      setIncidents(l.data.incidents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load incidents');
    } finally {
      setLoading(false);
    }
  }, [days, statusFilter, severityFilter, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Incident Reporting"
      subtitle="State-surveyor-ready incident log for falls, medication errors, near-misses, infections, and every other reportable event."
      testId="admin-incidents-page"
    >
      {/* HEADER STRIP */}
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="incidents-controls">
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Window</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="incidents-window">
            {[7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="incidents-status-filter">
            <option value="">All</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Severity</span>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="incidents-severity-filter">
            <option value="">All</option>
            {Object.entries(SEVERITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="incidents-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <div className="ml-auto flex gap-2">
          <Button onClick={downloadPdf} disabled={downloadingPdf || loading} className="cn-btn cn-btn-ghost !py-2" data-testid="incidents-export-pdf">
            <FileDown className={`w-4 h-4 ${downloadingPdf ? 'animate-pulse' : ''}`} />
            {downloadingPdf ? 'Building…' : 'Export PDF'}
          </Button>
          <Button onClick={() => setShowCreate(true)} className="cn-btn cn-btn-primary !py-2" data-testid="incident-create-btn">
            <Plus className="w-4 h-4" /> Report incident
          </Button>
        </div>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && dashboard && (
        <>
          {/* HEADLINE KPIs */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="incidents-kpis">
            <KpiTile label="Total" value={dashboard.total} icon={ClipboardList} sub={`Last ${dashboard.window_days} days`} testid="incidents-kpi-total" />
            <KpiTile label="Open" value={dashboard.open_count} icon={AlertTriangle} sub="Reported + under review" tone={dashboard.open_count > 0 ? 'amber' : 'emerald'} testid="incidents-kpi-open" />
            <KpiTile label="Serious (sev 4–5)" value={dashboard.serious_count} icon={ShieldAlert} sub="Demand external follow-up" tone={dashboard.serious_count > 0 ? 'red' : 'emerald'} testid="incidents-kpi-serious" />
            <KpiTile label="External report req." value={dashboard.external_report_count} icon={FileText} sub="State agency / APS / CMS" tone={dashboard.external_report_count > 0 ? 'amber' : 'emerald'} testid="incidents-kpi-external" />
          </div>

          {/* BY SEVERITY */}
          <div className="cn-card p-5 mb-6" data-testid="incidents-by-severity">
            <h4 className="font-display font-semibold text-base text-cn-text mb-3">Severity distribution</h4>
            <div className="grid grid-cols-5 gap-3">
              {Object.entries(SEVERITY_LABELS).map(([sev, lbl]) => {
                const count = dashboard.by_severity[sev] || 0;
                const max = Math.max(...Object.values(dashboard.by_severity)) || 1;
                return (
                  <div key={sev} className="text-center" data-testid={`severity-bucket-${sev}`}>
                    <div className="h-24 flex items-end justify-center mb-1">
                      <div className={`w-10 rounded-t-lg ${SEVERITY_PILL[Number(sev)].split(' ').find((c) => c.startsWith('bg-'))}`} style={{ height: `${Math.max(4, (count / max) * 80)}px` }} />
                    </div>
                    <p className="font-display font-bold text-lg text-cn-text">{count}</p>
                    <p className="text-[10px] text-cn-text-muted leading-tight">{lbl.split(' · ')[1]}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* INCIDENTS TABLE */}
          <div className="cn-card overflow-hidden" data-testid="incidents-table-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base text-cn-text">Incidents ({incidents.length})</h4>
            </div>
            {incidents.length === 0 ? (
              <div className="p-10 text-center" data-testid="incidents-empty">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm text-cn-text-muted">No incidents match the current filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="incidents-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">When</th>
                      <th className="px-3 py-3">Type</th>
                      <th className="px-3 py-3">Severity</th>
                      <th className="px-3 py-3">Where</th>
                      <th className="px-3 py-3">Reported by</th>
                      <th className="px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {incidents.map((i) => (
                      <tr key={i.id} className="hover:bg-cn-bone-50/40 cursor-pointer" onClick={() => setFocus(i)} data-testid={`incident-row-${i.id}`}>
                        <td className="px-5 py-2.5">{i.occurred_at ? new Date(i.occurred_at).toLocaleString() : '—'}</td>
                        <td className="px-3 py-2.5">{INCIDENT_TYPES.find((t) => t.value === i.incident_type)?.label || i.incident_type}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${SEVERITY_PILL[Number(i.severity)] || ''}`}>
                            {SEVERITY_LABELS[i.severity]?.split(' · ')[0] || i.severity}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-cn-text-muted">{i.location}</td>
                        <td className="px-3 py-2.5">{i.reported_by_name || '—'}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${STATUS_PILL[i.status]}`}>{STATUS_LABEL[i.status] || i.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showCreate && <CreateIncidentModal onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await load(); }} />}
      {focus && <IncidentDetailModal incident={focus} onClose={() => setFocus(null)} onUpdated={async () => { setFocus(null); await load(); }} />}
    </DashboardShell>
  );
}

function KpiTile({ label, value, icon: Icon, sub, tone = 'neutral', testid }) {
  const iconCls = {
    neutral: 'text-cn-primary',
    amber: 'text-amber-600',
    red: 'text-red-600',
    emerald: 'text-emerald-600',
  }[tone];
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${iconCls}`} />
      </div>
      <p className="font-display font-bold text-3xl text-cn-text dark:text-white">{value}</p>
      <p className="text-xs text-cn-text-muted mt-1">{sub}</p>
    </div>
  );
}

function CreateIncidentModal({ onClose, onCreated }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({
    incident_type: 'fall',
    severity: 2,
    occurred_at: new Date().toISOString().slice(0, 16),
    location: '',
    narrative: '',
    immediate_action: '',
    patient_initials: '',
    supervisor_notified: false,
    requires_external_report: false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.location.trim() || form.narrative.trim().length < 10) {
      toast.error('Location and narrative (≥10 chars) are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        occurred_at: new Date(form.occurred_at).toISOString(),
        witnesses: [],
      };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      await axios.post(`${API}/incidents`, payload, getAuthHeader());
      toast.success('Incident reported');
      await onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not file incident');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="incident-create-modal">
      <div className="cn-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg text-cn-text">Report incident</h3>
          <button onClick={onClose} className="text-cn-text-muted hover:text-cn-text"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-cn-text-muted mb-4">All entries are tamper-evident and appear in the state-surveyor audit trail.</p>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-cn-text-muted">Type</label>
              <select value={form.incident_type} onChange={(e) => setForm({ ...form, incident_type: e.target.value })} className="w-full border border-cn-border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="incident-form-type">
                {INCIDENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-cn-text-muted">Severity (CMS scale)</label>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: Number(e.target.value) })} className="w-full border border-cn-border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="incident-form-severity">
                {Object.entries(SEVERITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">When did it occur?</label>
            <Input type="datetime-local" value={form.occurred_at} onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} data-testid="incident-form-occurred" />
          </div>
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">Where (room, address, vehicle…)</label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Patient bathroom · 123 Oak St" data-testid="incident-form-location" />
          </div>
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">Patient initials (no PHI)</label>
            <Input value={form.patient_initials} onChange={(e) => setForm({ ...form, patient_initials: e.target.value.slice(0, 6) })} placeholder="e.g. J.S." maxLength={6} data-testid="incident-form-patient-initials" />
          </div>
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">Narrative (factual, time-stamped)</label>
            <textarea value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} rows={5} className="w-full border border-cn-border rounded-lg px-3 py-2 text-sm" placeholder="At 14:32 the patient lost balance while transferring from bed to commode. Caught by clinician, lowered to the floor without injury. Vitals stable…" data-testid="incident-form-narrative" />
          </div>
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">Immediate action taken</label>
            <textarea value={form.immediate_action} onChange={(e) => setForm({ ...form, immediate_action: e.target.value })} rows={2} className="w-full border border-cn-border rounded-lg px-3 py-2 text-sm" placeholder="Notified MD on call, completed neuro check, fall-precaution flag added to plan of care." data-testid="incident-form-immediate-action" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-cn-text" data-testid="incident-form-supervisor-label">
              <input type="checkbox" checked={form.supervisor_notified} onChange={(e) => setForm({ ...form, supervisor_notified: e.target.checked })} data-testid="incident-form-supervisor-checkbox" />
              Supervisor notified
            </label>
            <label className="flex items-center gap-2 text-sm text-cn-text" data-testid="incident-form-external-label">
              <input type="checkbox" checked={form.requires_external_report} onChange={(e) => setForm({ ...form, requires_external_report: e.target.checked })} data-testid="incident-form-external-checkbox" />
              Requires external report (state agency / APS / CMS)
            </label>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <Button onClick={onClose} className="cn-btn cn-btn-ghost" data-testid="incident-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="cn-btn cn-btn-primary" data-testid="incident-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            File incident
          </Button>
        </div>
      </div>
    </div>
  );
}

function IncidentDetailModal({ incident, onClose, onUpdated }) {
  const { getAuthHeader } = useAuth();
  const [note, setNote] = useState('');
  const [status, setStatus] = useState(incident.status);
  const [saving, setSaving] = useState(false);

  const submitFollowUp = async () => {
    if (note.trim().length < 2) return;
    setSaving(true);
    try {
      await axios.post(`${API}/incidents/${incident.id}/follow-up`, { note: note.trim() }, getAuthHeader());
      toast.success('Follow-up added');
      setNote('');
      await onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add follow-up');
    } finally {
      setSaving(false);
    }
  };

  const submitStatus = async () => {
    if (status === incident.status) return onClose();
    setSaving(true);
    try {
      await axios.patch(`${API}/incidents/${incident.id}`, { status }, getAuthHeader());
      toast.success('Status updated');
      await onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="incident-detail-modal">
      <div className="cn-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-semibold text-lg text-cn-text">{INCIDENT_TYPES.find((t) => t.value === incident.incident_type)?.label}</h3>
            <p className="text-xs text-cn-text-muted">Filed {new Date(incident.created_at).toLocaleString()} by {incident.reported_by_name}</p>
          </div>
          <button onClick={onClose} className="text-cn-text-muted hover:text-cn-text"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
          <Field label="When">{new Date(incident.occurred_at).toLocaleString()}</Field>
          <Field label="Where">{incident.location}</Field>
          <Field label="Severity"><span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${SEVERITY_PILL[Number(incident.severity)]}`}>{SEVERITY_LABELS[incident.severity]}</span></Field>
          <Field label="Patient">{incident.patient_initials || '—'}</Field>
          <Field label="Supervisor notified">{incident.supervisor_notified ? 'Yes' : 'No'}</Field>
          <Field label="External report">{incident.requires_external_report ? 'Required' : 'No'}</Field>
        </div>

        <div className="cn-card p-3 bg-cn-bone-50 mb-4" data-testid="incident-narrative">
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">Narrative</p>
          <p className="text-sm text-cn-text whitespace-pre-line">{incident.narrative}</p>
        </div>

        {incident.immediate_action && (
          <div className="cn-card p-3 bg-cn-bone-50 mb-4" data-testid="incident-immediate-action">
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">Immediate action</p>
            <p className="text-sm text-cn-text whitespace-pre-line">{incident.immediate_action}</p>
          </div>
        )}

        {/* STATUS UPDATE */}
        <div className="flex items-center gap-2 mb-4" data-testid="incident-status-row">
          <span className="text-xs font-semibold text-cn-text-muted">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-cn-border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="incident-status-select">
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Button onClick={submitStatus} disabled={saving || status === incident.status} className="cn-btn cn-btn-primary !py-1.5 text-xs" data-testid="incident-status-save">
            Update
          </Button>
        </div>

        {/* FOLLOW-UPS */}
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Follow-ups ({(incident.follow_ups || []).length})</p>
          {(incident.follow_ups || []).length === 0 ? (
            <p className="text-xs text-cn-text-muted">No follow-ups yet.</p>
          ) : (
            <div className="space-y-2" data-testid="incident-follow-ups">
              {incident.follow_ups.map((f) => (
                <div key={f.id} className="text-sm border-l-2 border-cn-primary/30 pl-3" data-testid={`follow-up-${f.id}`}>
                  <p className="text-cn-text">{f.note}</p>
                  <p className="text-[10px] text-cn-text-muted mt-0.5">{f.by_user_name} · {new Date(f.at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a follow-up note…" data-testid="incident-follow-up-input" />
            <Button onClick={submitFollowUp} disabled={saving || note.trim().length < 2} className="cn-btn cn-btn-ghost" data-testid="incident-follow-up-add">
              <TrendingUp className="w-4 h-4" /> Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
      <div className="text-sm text-cn-text">{children}</div>
    </div>
  );
}
